import { BALANCE, TICK_S, colOf, xOf, yOf } from '../constants';
import type { Simulation } from '../sim';
import type { Peep } from '../types';
import { FLAG_BUILDING, FLAG_PAVED, FLAG_ROAD } from '../types';
import { findPath, accessTiles } from '../world/path';
import { digWorkTiles, hasFlag } from '../world/terrain';
import { BUILDING_DEFS } from '../constants';
import { releaseTaskClaims } from './claims';
import { tierSpeed } from './tiers';

const scratchWorkTiles: number[] = [];

function currentTargets(sim: Simulation, peep: Peep): number | ReadonlySet<number> | null {
  const state = sim.state;
  switch (peep.task.kind) {
    case 'toDig': {
      const tiles = digWorkTiles(state.terrain, peep.task.col, scratchWorkTiles);
      return tiles.length > 0 ? new Set(tiles) : null;
    }
    case 'toPile':
    case 'toGoodsPile':
      return peep.task.col;
    case 'toGoodsDropoff':
    case 'toOutputPickup': {
      const building = state.buildings.find(
        (candidate) => candidate.id === (peep.task as { buildingId: number }).buildingId,
      );
      if (!building) return null;
      const def = BUILDING_DEFS[building.type];
      return accessTiles(state.terrain, building.x, building.y, def.w, def.h);
    }
    case 'toDepot': {
      const depot = state.buildings.find((building) => building.type === 'oreDepot');
      if (!depot) return null;
      const def = BUILDING_DEFS.oreDepot;
      return accessTiles(state.terrain, depot.x, depot.y, def.w, def.h);
    }
    case 'staffing': {
      const building = state.buildings.find((candidate) => candidate.id === (peep.task as { buildingId: number }).buildingId);
      if (!building) return null;
      const def = BUILDING_DEFS[building.type];
      return accessTiles(state.terrain, building.x, building.y, def.w, def.h);
    }
    default:
      return null;
  }
}

function abandonTask(sim: Simulation, peep: Peep): void {
  releaseTaskClaims(sim.state, peep);
  peep.task = { kind: 'idle' };
  peep.path = null;
}

function repath(sim: Simulation, peep: Peep): void {
  const targets = currentTargets(sim, peep);
  const path = targets === null ? null : findPath(sim.state.terrain, colOf(Math.floor(peep.x), Math.floor(peep.y)), targets);
  if (path === null) {
    peep.repathFailures += 1;
    if (peep.repathFailures >= 3) abandonTask(sim, peep);
    else peep.path = null; // jobs/task systems will retry
    return;
  }
  peep.path = path.length > 0 ? path : null;
  peep.pathIdx = 0;
  peep.pathTerrainVersion = sim.state.terrain.version;
  peep.repathFailures = 0;
}

/** Advance every peep along its path; validates each edge against live terrain. */
export function stepMovement(sim: Simulation): void {
  const state = sim.state;
  const terrain = state.terrain;
  for (const peep of state.peeps) {
    peep.px = peep.x;
    peep.py = peep.y;
    if (peep.path === null) continue;

    let remaining = TICK_S;
    while (remaining > 1e-6 && peep.path !== null) {
      const nextCol = peep.path[peep.pathIdx];
      if (nextCol === undefined) {
        peep.path = null;
        break;
      }
      const standCol = colOf(Math.floor(peep.x), Math.floor(peep.y));
      const standDepth = terrain.depth[standCol] ?? 0;
      const nextDepth = terrain.depth[nextCol] ?? 0;
      const depthDelta = Math.abs(nextDepth - standDepth);
      if (hasFlag(terrain, nextCol, FLAG_BUILDING)) {
        repath(sim, peep);
        break;
      }
      let speed = BALANCE.peepSpeedTilesPerSec * tierSpeed(peep);
      if (peep.role === 'child') speed *= 0.8;
      if (peep.carryDt > 0) speed *= BALANCE.carrySpeedFactor;
      if (depthDelta > 1) speed *= BALANCE.climbSpeedFactor;
      else if (depthDelta === 1) speed *= BALANCE.slopeSpeedFactor;
      else if (hasFlag(terrain, standCol, FLAG_ROAD) && hasFlag(terrain, nextCol, FLAG_ROAD)) {
        const paved = hasFlag(terrain, standCol, FLAG_PAVED) && hasFlag(terrain, nextCol, FLAG_PAVED);
        speed *= paved ? BALANCE.pavedSpeedFactor : BALANCE.roadSpeedFactor;
      }
      const targetX = xOf(nextCol) + 0.5;
      const targetY = yOf(nextCol) + 0.5;
      const dx = targetX - peep.x;
      const dy = targetY - peep.y;
      const distance = Math.hypot(dx, dy);
      const step = speed * remaining;
      if (step >= distance) {
        peep.x = targetX;
        peep.y = targetY;
        remaining -= distance / speed;
        peep.pathIdx += 1;
        if (peep.pathIdx >= peep.path.length) peep.path = null;
      } else {
        peep.x += (dx / distance) * step;
        peep.y += (dy / distance) * step;
        remaining = 0;
      }
    }
  }
}
